/**
 * Quiz application factory for Alpine.js
 * Manages quiz flow, scoring, and results calculation
 * @param {Object} config - Configuration object
 * @param {Object} config.categories - Categories configuration
 * @param {Array} config.questions - Quiz questions array
 */
export default (config = {}) => {
	const defaultConfig = {
		categories : {
			technical : {
				name      : 'Технические',
				score     : 0,
				className : 'technical',
			},
			engineering : {
				name      : 'Инженерные',
				score     : 0,
				className : 'engineering',
			},
			analytical : {
				name      : 'Аналитические',
				score     : 0,
				className : 'analytical',
			},
			social : {
				name      : 'Социальные',
				score     : 0,
				className : 'social',
			},
			creative : {
				name      : 'Творческие',
				score     : 0,
				className : 'creative',
			},
		},
		questions : [],
	};

	// Merge config with defaults
	const finalConfig = {
		categories : { ...defaultConfig.categories, ...config.categories },
		questions  : config.questions,
	};

	return ({
		// Categories configuration
		categories : finalConfig.categories,

		// Quiz questions data
		questions : finalConfig.questions,

		// State
		currentStep     : 0,
		selectedAnswers : [],
		isCompleted     : false,

		/**
         * Initialize quiz on component mount
         */
		init() {
			this.resetQuiz();
		},

		/**
         * Reset quiz to initial state
         */
		resetQuiz() {
			this.currentStep     = 0;
			this.selectedAnswers = new Array(this.questions.length).fill(null);
			this.isCompleted     = false;
			Object.keys(this.categories).forEach((key) => {
				this.categories[key].score = 0;
			});
		},

		/**
         * Get current question
         */
		get currentQuestion() {
			return this.questions[this.currentStep];
		},

		/**
         * Get progress percentage
         */
		get progressPercent() {
			if (this.currentStep === 0) return 0;
			if (this.currentStep === this.questions.length - 1) return 95;
			return Math.round((this.currentStep / (this.questions.length - 1)) * 95);
		},

		/**
         * Check if answer is selected for current question
         */
		get isCurrentAnswerSelected() {
			return this.selectedAnswers[this.currentStep] !== null;
		},

		/**
         * Check if it's the first step
         */
		get isFirstStep() {
			return this.currentStep === 0;
		},

		/**
         * Check if it's the last step
         */
		get isLastStep() {
			return this.currentStep === this.questions.length - 1;
		},

		/**
         * Select answer for current question
         */
		selectAnswer(answerIndex) {
			this.selectedAnswers[this.currentStep] = answerIndex;
			this.$nextTick(() => {
				const rootEl     = this.$root || document.querySelector('[data-x-data="quizApp"]');
				const nextButton = rootEl?.querySelector('.test__btn-next');
				if (nextButton) {
					nextButton.focus();
				}
			});
		},

		/**
         * Navigate to next question with animation
         */
		nextStep() {
			if (!this.isCurrentAnswerSelected) return;

			if (this.isLastStep) {
				this.animateTransition(() => {
					this.completeQuiz();
				});
			} else {
				this.animateTransition(() => {
					this.currentStep += 1;
					this.focusFirstRadio();
				});
			}
		},

		/**
         * Navigate to previous question with animation
         */
		prevStep() {
			if (this.currentStep > 0) {
				this.animateTransition(() => {
					this.currentStep -= 1;
					this.focusFirstRadio();
				});
			}
		},

		/**
         * Focus first radio button for accessibility
         */
		focusFirstRadio() {
			this.$nextTick(() => {
				const rootEl     = this.$root || document.querySelector('[data-x-data="quizApp"]');
				const firstRadio = rootEl?.querySelector('input[type="radio"]');
				if (firstRadio) {
					firstRadio.focus();
				}
			});
		},

		/**
         * Animate transition using View Transition API
         */
		animateTransition(callback) {
			// Check if View Transition API is supported
			if (document.startViewTransition) {
				document.startViewTransition(() => {
					callback();
				});
			} else {
				// Fallback for browsers without View Transition API
				callback();
			}
		},

		/**
         * Complete quiz and calculate results
         */
		completeQuiz() {
			// Reset scores
			Object.keys(this.categories).forEach((key) => {
				this.categories[key].score = 0;
			});

			// Calculate scores
			this.selectedAnswers.forEach((answerIndex, questionIndex) => {
				if (answerIndex !== null) {
					const { category }                = this.questions[questionIndex].answers[answerIndex];
					this.categories[category].score = this.categories[category].score + 1;
				}
			});

			this.isCompleted = true;
		},

		/**
         * Get results with percentages
         */
		get results() {
			const totalQuestions = this.questions.length;
			const results        = [];

			Object.keys(this.categories).forEach((key) => {
				const category = this.categories[key];
				const percent  = Math.round((category.score / totalQuestions) * 100);

				const visualWidth = Math.max(percent, 5);

				results.push({
					key,
					name      : category.name,
					className : category.className,
					score     : category.score,
					percent,
					visualWidth,
				});
			});

			// Sort by score descending
			results.sort((a, b) => b.score - a.score);

			return results;
		},

		/**
		 * Get top 2 categories
		 */
		get topCategories() {
			return this.results.slice(0, 2);
		},

		/**
		 * Get category order for CSS grid sorting
		 * Returns object with category keys and their order values
		 */
		get categoryOrder() {
			const order = {};
			this.results.forEach((result, index) => {
				order[result.key] = index;
			});
			return order;
		},

		// === Binding objects for Alpine.js ===

		/**
         * Bindings for quiz form container
         */
		quizForm : {
			'data-x-show' : function () {
				return !this.isCompleted;
			},
		},

		/**
         * Bindings for results container
         */
		resultsContainer : {
			'data-x-show' : function () {
				return this.isCompleted;
			},
		},

		/**
         * Bindings for question title
         */
		questionTitle : {
			'data-x-html' : function () {
				return this.currentQuestion.text;
			},
		},

		/**
         * Bindings for progress line
         */
		progressLine : {
			'data-x-bind:style' : function () {
				return `width: ${this.progressPercent}%`;
			},
		},

		/**
         * Bindings for progress text
         */
		progressText : {
			'data-x-text' : function () {
				return `${this.progressPercent}%`;
			},
		},

		/**
         * Bindings for previous button
         */
		prevButton : {
			'data-x-on:click.prevent' : function () {
				this.prevStep();
			},
			'data-x-show' : function () {
				return !this.isFirstStep;
			},
		},

		/**
         * Bindings for next button
         */
		nextButton : {
			'data-x-on:click.prevent' : function () {
				this.nextStep();
			},
			'data-x-bind:disabled' : function () {
				return !this.isCurrentAnswerSelected;
			},
			'data-x-bind:class' : function () {
				return {
					disabled : !this.isCurrentAnswerSelected,
				};
			},
		},
	});
};
